/**
 *  ClassifyPanel.jsx
 *  @description: Clasifica un reporte rápido de la app (llega con área,
 *  equipo y problema "Por clasificar"). Los selects van encadenados como en
 *  la app: áreas del tipo de unidad → equipos del área → problemas del equipo.
 *  El servidor vuelve a validar la combinación contra el tipo de unidad.
 */
import React, { useEffect, useState } from 'react';
import cogoToast from 'cogo-toast';

import api from '../../../core/apiV1';
import { toLegacy } from '../../../core/bridge/requests';

const ClassifyPanel = ({ solicitud, onClassified }) => {
   const [areas, setAreas] = useState(null); // árbol del tipo de unidad
   const [areaId, setAreaId] = useState('');
   const [equipmentId, setEquipmentId] = useState('');
   const [problems, setProblems] = useState([]);
   const [problemId, setProblemId] = useState('');
   const [saving, setSaving] = useState(false);

   useEffect(() => {
      let alive = true;
      setAreas(null);
      setAreaId('');
      setEquipmentId('');
      setProblemId('');
      (async () => {
         const projectId = solicitud.IdProyecto;
         const [units, types] = await Promise.all([
            api.get(`projects/${projectId}/units`),
            api.get(`projects/${projectId}/unit-types`),
         ]);
         if (!alive) return;
         const unit = units.ok && (units.data || []).find((u) => u.id === Number(solicitud.IdUnidad));
         const type = unit && types.ok && (types.data || []).find((t) => t.id === unit.unitTypeId);
         setAreas(type ? type.areas || [] : []);
      })();
      return () => {
         alive = false;
      };
   }, [solicitud.IdSolicitud, solicitud.IdProyecto, solicitud.IdUnidad]);

   useEffect(() => {
      let alive = true;
      setProblems([]);
      setProblemId('');
      if (!equipmentId) return undefined;
      (async () => {
         const res = await api.get(`catalogs/equipment/${equipmentId}/problems`);
         if (alive && res.ok) setProblems(res.data || []);
      })();
      return () => {
         alive = false;
      };
   }, [equipmentId]);

   const area = (areas || []).find((a) => a.areaId === Number(areaId));
   const equipment = area ? area.equipment || [] : [];

   const save = async () => {
      if (!areaId || !equipmentId || !problemId) {
         cogoToast.warn('Elige área, equipo y problema.', { position: 'top-right' });
         return;
      }
      setSaving(true);
      const res = await api.patch(`requests/${solicitud.IdSolicitud}/classification`, {
         areaId: Number(areaId),
         equipmentId: Number(equipmentId),
         problemId: Number(problemId),
      });
      setSaving(false);
      if (!res.ok) {
         cogoToast.error(res.message || 'No se pudo clasificar el reporte.', { position: 'top-right' });
         return;
      }
      cogoToast.success('Reporte clasificado.', { position: 'top-right' });
      onClassified(toLegacy(res.data));
   };

   return (
      <div className="classify-panel">
         <p className="weight-bold">
            <i className="fas fa-exclamation-triangle"></i>&nbsp; Reporte rápido: falta clasificarlo
         </p>
         {areas === null ? (
            <i className="fas fa-spinner fa-spin"></i>
         ) : areas.length === 0 ? (
            <p className="font-mini">
               El tipo de unidad no tiene áreas configuradas. Agrégalas en Configuración → Proyectos.
            </p>
         ) : (
            <div className="classify-row">
               <select className="input input-modals" value={areaId} onChange={(e) => { setAreaId(e.target.value); setEquipmentId(''); }}>
                  <option value="">Área</option>
                  {areas.map((a) => (
                     <option key={a.id} value={a.areaId}>
                        {a.name && a.name.es}
                     </option>
                  ))}
               </select>
               <select className="input input-modals" value={equipmentId} disabled={!areaId} onChange={(e) => setEquipmentId(e.target.value)}>
                  <option value="">Equipo</option>
                  {equipment.map((q) => (
                     <option key={q.id} value={q.equipmentId}>
                        {q.name && q.name.es}
                     </option>
                  ))}
               </select>
               <select className="input input-modals" value={problemId} disabled={!equipmentId} onChange={(e) => setProblemId(e.target.value)}>
                  <option value="">Problema</option>
                  {problems.map((p) => (
                     <option key={p.id} value={p.problemId}>
                        {p.name}
                     </option>
                  ))}
               </select>
               <button type="button" className="btn btn-primary btn-small color-white" disabled={saving} onClick={save}>
                  {saving ? <i className="fas fa-spinner fa-spin"></i> : 'Clasificar'}
               </button>
            </div>
         )}
      </div>
   );
};

export default ClassifyPanel;
